
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ReferralData } from '@/services/firebase';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface ReferralTableProps {
  referrals: ReferralData[];
  isAdmin?: boolean;
  onUpdateStatus?: (id: string, status: ReferralData['status'], commission?: number) => void;
  pagination: {
    currentPage: number;
    hasMore: boolean;
    onPrevious: () => void;
    onNext: () => void;
  };
}

const statusColors = {
  'new': 'bg-blue-500',
  'contacted': 'bg-yellow-500',
  'in-progress': 'bg-purple-500',
  'sold': 'bg-green-500',
  'cancelled': 'bg-red-500'
};

const ReferralTable: React.FC<ReferralTableProps> = ({ 
  referrals, 
  isAdmin = false,
  onUpdateStatus,
  pagination
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [commissionValue, setCommissionValue] = useState<string>('');
  
  const handleStatusChange = (id: string, status: ReferralData['status']) => {
    if (status === 'sold' && !editingId) {
      setEditingId(id);
      setCommissionValue('');
    } else if (onUpdateStatus) {
      onUpdateStatus(id, status);
    }
  };
  
  const handleCommissionSubmit = (id: string) => {
    if (onUpdateStatus) {
      const commission = parseFloat(commissionValue);
      if (!isNaN(commission)) {
        onUpdateStatus(id, 'sold', commission);
      }
      setEditingId(null);
      setCommissionValue('');
    }
  };
  
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  return (
    <div className="w-full overflow-auto rounded-lg glass-panel">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Contact</TableHead>
            {isAdmin && <TableHead>Referrer Code</TableHead>}
            <TableHead>Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Commission</TableHead>
            {isAdmin && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {referrals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8">
                No referrals found
              </TableCell>
            </TableRow>
          ) : (
            referrals.map((referral) => (
              <TableRow key={referral.id} className="hover:bg-secondary/50 transition-colors">
                <TableCell className="font-medium">
                  {referral.firstName} {referral.lastName}
                  {referral.notes && (
                    <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]">
                      {referral.notes}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{referral.phone}</span>
                    <span className="text-xs text-muted-foreground">{referral.address}</span>
                  </div>
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {referral.referrerCode}
                    </Badge>
                  </TableCell>
                )}
                <TableCell>{formatDate(referral.createdAt)}</TableCell>
                <TableCell>
                  <Badge className={`${statusColors[referral.status]}`}>
                    {referral.status.charAt(0).toUpperCase() + referral.status.slice(1).replace('-', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  {editingId === referral.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={commissionValue}
                        onChange={(e) => setCommissionValue(e.target.value)}
                        className="w-20 p-1 rounded bg-input border-border border text-foreground"
                        placeholder="0.00"
                        step="0.01"
                      />
                      <Button 
                        size="sm" 
                        variant="default"
                        onClick={() => handleCommissionSubmit(referral.id!)}
                      >
                        Save
                      </Button>
                    </div>
                  ) : (
                    <span>
                      {referral.commission 
                        ? `$${referral.commission.toFixed(2)}` 
                        : referral.status === 'sold' 
                          ? '$0.00' 
                          : '-'
                      }
                    </span>
                  )}
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    <div className="flex space-x-2">
                      <select
                        className="bg-secondary border border-border rounded p-1 text-sm"
                        value={referral.status}
                        onChange={(e) => handleStatusChange(referral.id!, e.target.value as ReferralData['status'])}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="in-progress">In Progress</option>
                        <option value="sold">Sold</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      
      <div className="flex items-center justify-between px-4 py-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Page {pagination.currentPage}
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={pagination.onPrevious}
            disabled={pagination.currentPage === 1}
          >
            <FaChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={pagination.onNext}
            disabled={!pagination.hasMore}
          >
            <FaChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralTable;
